/**
 * "What's an LMN?" — info button + modal explaining Letter of Medical Necessity.
 * Used on PLP/carousel LMN tiles and on PDP for LMN products.
 *
 * Content (title / body / "Learn More" link) is authored per-site in the Builder
 * `site-preferences` entry and threaded in via props (resolved once in the layout
 * loader, read from `useLayoutData().lmnContent` by callers — see SFCC-11677).
 * The local defaults are a last-resort safety net for render paths without loader
 * data (e.g. isolated tests); the loader always supplies the real copy.
 *
 * Source reference: hec-composable/hec/builder/blocks/plp/categoryProductGrid.jsx
 */
import { useState } from "react";

import { Dialog, DialogContent } from "~/components/ui/dialog";

import {
  LMN_FALLBACK_MODAL_CONTENT,
  LMN_FALLBACK_MODAL_TITLE,
} from "./lmn-content.constants";

export function WhatsLmnButton({
  title,
  content,
  link,
  className,
  "data-testid": dataTestId,
}: {
  /** Modal heading — Builder `lmnModalTitle`. */
  title?: string;
  /** Modal body (newlines preserved) — Builder `lmnModalContent`. */
  content?: string;
  /** Optional "Learn More" target — Builder `lmnModalLink`. */
  link?: string;
  className?: string;
  "data-testid"?: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const modalTitle = title?.trim() || LMN_FALLBACK_MODAL_TITLE;
  const modalContent = content?.trim() || LMN_FALLBACK_MODAL_CONTENT;

  return (
    <>
      <button
        type="button"
        className={className ?? "cursor-pointer text-sm text-accent underline"}
        onClick={() => setModalOpen(true)}
        data-testid={dataTestId || "lmn-whats-lmn"}
      >
        What&apos;s an LMN?
      </button>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md bg-white p-6">
          <h3 className="mb-2 text-lg font-semibold">{modalTitle}</h3>
          {/* whitespace-pre-line preserves the authored paragraph breaks */}
          <p className="whitespace-pre-line text-sm">{modalContent}</p>
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block text-sm font-medium text-accent underline"
            >
              Learn More
            </a>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
